import classnames from 'classnames';
import { any, assoc, equals, find, length, map, propEq } from 'ramda';
import React, { CSSProperties, FC, MouseEvent, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsync } from 'react-use';
import { LassoItemMargin } from '../../../../../constants/Lasso';
import { Layout } from '../../../../../constants/Layout';
import { ModalName } from '../../../../../constants/ModalName';
import { ExternalSource } from '../../../../../constants/Source';
import { SystemFolderId } from '../../../../../constants/SystemFolderId';
import { EmptyState } from '../../../../../containers/FilesImporter/components/FilesViewer/EmptyState';
import filesViewerStyles from '../../../../../containers/FilesImporter/components/FilesViewer/FilesViewer.scss';
import { FilesGallery } from '../../../../../containers/FilesImporter/components/FilesViewer/layouts/FilesGalleryLayout';
import { MediaTypeSelect } from '../../../../../containers/FilesManager/MediaTypeSelect';
import { BI_ERRORS, errorLogger } from '../../../../../services/loggers/errorLogger';
import { useAdapter } from '../../../../../utils/hooks/useAdapter';
import { useBoards } from '../../../../../utils/hooks/useBoards';
import { useEffectButSkipMount } from '../../../../../utils/hooks/useEffectButSkipMount';
import { useModalHandlersAndOptions } from '../../../../../utils/hooks/useModal';
import { MediaManagerSDKAnyItem } from '../../../../../utils/media-manager-sdk';
import { isCurrentSiteOnlyBoard } from '../../../../../utils/media-manager-sdk/boards';
import { parseSiteMediaToken } from '../../../../../utils/siteMediaToken';
import { Breadcrumbs } from '../../../Breadcrumbs';
import { FullHeightLoader } from '../../../FullHeightLoader';
import { AutoComplete, Button } from '../../../index';
import { Lasso } from '../../../Lasso';
import lassoSelectedAreaStyles from '../../../Lasso/withLassoSelectedAreaStyle/LassoSelectedArea.scss';
import { Loader } from '../../../Loader';
import { Search } from '../../../Search';
import styles from './SiteFilesViewer.scss';
import { IInner, IOuter } from './SiteFilesViewer.types';
import { Text, listItemSectionBuilder } from '@wix/design-system';
import { origins } from '@wix/media-manager-kit';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';

interface ISite {
  id: string;
  value: string;
  siteMediaToken: string;
}

interface ISiteListState {
  wixSitesList: ISite[];
  wixelProjectsList: ISite[];
  activeSite: ISite;
}

const enum ActionType {
  SetSiteLists = 'set-site-lists',
  SetActiveSite = 'set-active-site',
  AddMoreWixSites = 'add-more-wix-sites',
  AddMoreWixelProjects = 'add-more-wixel-projects',
}

const reducer = (state: ISiteListState, action) => {
  switch (action.type) {
    case ActionType.SetSiteLists:
      return action.payload;

    case ActionType.SetActiveSite:
      return { ...state, activeSite: action.payload };

    case ActionType.AddMoreWixSites:
      return { ...state, wixSitesList: [...state.wixSitesList, ...action.payload] };

    case ActionType.AddMoreWixelProjects:
      return { ...state, wixelProjectsList: [...state.wixelProjectsList, ...action.payload] };

    default:
      return state;
  }
};

const scrollStyle: CSSProperties = { position: 'relative', overflowX: 'hidden', flexDirection: 'column' };

const parseSiteListData = map(({ id, name, siteId }) => ({
  id: siteId,
  value: name,
  siteMediaToken: JSON.parse(id).siteMediaToken,
}));

export const SiteFilesViewerComponent: FC<IOuter & IInner> = ({
  source,
  selectedItems,
  refreshFolder,
  openFolder: openFolderFromProps,
  className,
  folderPath,
  items,
  isLoading,
  isSearch,
  toggleItemSelection,
  selectItemsUntil,
  selectItem,
  selectItems,
  mediaTypes,
  setMediaType,
  areNotAllMediaTypesSelected,
  selectAllMediaTypes,
  areNextFilesLoading,
  hasMoreItems,
  loadMoreItems,
  selectedItemsCount,
  isItemSelected,
  clearItemsSelection,
  isNotAuthorized,
  onSourceSessionExpired,
  searchQuery,
  setSearchQuery,
  clearSearch,
  updateSiteMediaToken,
  siteMediaToken,
  boards,
  onSelectItems,
}) => {
  const { mmgrSettings } = useMMGRSettings();
  const { isForceWixelImportEnabled, origin } = mmgrSettings;
  const isWixelEditor = origin === origins.WIXEL_EDITOR;

  const isWixelProjectsVisible = isWixelEditor || isForceWixelImportEnabled;

  useEffectButSkipMount(refreshFolder, [mediaTypes]);

  const {
    closeModal,
    additionalOptions: { shouldUpdateItemsInFolder, clickOrigin },
  } = useModalHandlersAndOptions(ModalName.AddFilesToBoard);

  const { addItems } = useBoards();
  const isSingleLevelNesting = equals(length(folderPath), 1);
  const isRoot = isSingleLevelNesting;

  const handleSelectItems = useCallback(
    (selectedItems) => {
      const itemsWithSiteToken = map<MediaManagerSDKAnyItem, MediaManagerSDKAnyItem>(
        assoc('siteMediaToken', siteMediaToken),
        selectedItems,
      );

      addItems(boards, itemsWithSiteToken, { clickOrigin })
        .then(() => {
          onSelectItems({ shouldUpdateItemsInFolder, items: selectedItems });
        })
        .catch(errorLogger.createLoggingHandler(BI_ERRORS.mediaManagerSDK.addItemsToBoard));

      closeModal();
    },
    [closeModal, clickOrigin, addItems, boards, shouldUpdateItemsInFolder, onSelectItems, siteMediaToken],
  );

  const { formatMessage } = useIntl();

  const wixSiteAdapter = useAdapter(ExternalSource.WixSite);
  const wixelAdapter = useAdapter(ExternalSource.Wixel);

  const { loading: isWixSitesLoading, value: wixSitesData } = useAsync(
    () => wixSiteAdapter.folders.list(SystemFolderId.WixSites, { includeCurrentSite: true }),
    [],
  );

  const { loading: isWixelProjectsLoading, value: wixelProjectsData } = useAsync(
    () => wixelAdapter.folders.list(SystemFolderId.WixSites, {}),
    [],
  );

  const isSitesListLoading = isWixSitesLoading || isWixelProjectsLoading;

  const [siteListState, dispatch] = useReducer(reducer, {
    wixSitesList: [],
    wixelProjectsList: [],
    activeSite: undefined,
  });
  const [fetchWixSitesCursor, setFetchWixSitesCursor] = useState(null);
  const [fetchWixelProjectsCursor, setFetchWixelProjectsCursor] = useState(null);

  useEffect(() => {
    if (isWixSitesLoading || isWixelProjectsLoading || !wixSitesData) {
      return;
    }
    setFetchWixSitesCursor(wixSitesData.paging?.cursor);
    setFetchWixelProjectsCursor(wixelProjectsData?.paging?.cursor);

    const parsedWixSites = parseSiteListData(wixSitesData.data);
    const parsedWixelProjects = wixelProjectsData?.data ? parseSiteListData(wixelProjectsData.data) : [];

    const currentSiteId = parseSiteMediaToken(siteMediaToken).metaSiteId;
    const activeSite =
      find(propEq('id', currentSiteId))(parsedWixSites) || find(propEq('id', currentSiteId))(parsedWixelProjects);

    dispatch({
      type: ActionType.SetSiteLists,
      payload: {
        wixSitesList: parsedWixSites,
        wixelProjectsList: parsedWixelProjects,
        activeSite,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWixSitesLoading, isWixelProjectsLoading, wixSitesData, wixelProjectsData, siteMediaToken]);

  const loadMoreWixSites = async () => {
    const moreSites = await wixSiteAdapter.folders.list(SystemFolderId.WixSites, {
      paging: { cursor: fetchWixSitesCursor },
    });
    setFetchWixSitesCursor(moreSites.paging?.cursor);
    dispatch({
      type: ActionType.AddMoreWixSites,
      payload: parseSiteListData(moreSites.data),
    });
  };

  const loadMoreWixelProjects = async () => {
    const moreProjects = await wixelAdapter.folders.list(SystemFolderId.WixSites, {
      paging: { cursor: fetchWixelProjectsCursor },
    });
    setFetchWixelProjectsCursor(moreProjects.paging?.cursor);
    dispatch({
      type: ActionType.AddMoreWixelProjects,
      payload: parseSiteListData(moreProjects.data),
    });
  };

  const loadMoreSites = async () => {
    if (fetchWixSitesCursor) {
      await loadMoreWixSites();
    } else if (fetchWixelProjectsCursor) {
      await loadMoreWixelProjects();
    }
  };

  const hasMoreSites = !!fetchWixSitesCursor || (isWixelProjectsVisible && !!fetchWixelProjectsCursor);

  const [siteSelectionState, updateSiteSelection] = useState('');

  const handleChangeSite = useCallback(
    (event) => {
      updateSiteSelection(event.target.value);
    },
    [updateSiteSelection],
  );

  // Combine Wix sites and Wixel projects into dropdown options with section headers
  const combinedOptions = useMemo(() => {
    const options = [];
    const filterValue = siteSelectionState.toLowerCase();

    const filteredWixSites = siteListState.wixSitesList.filter(
      (site) => `${site.value}`.toLowerCase().indexOf(filterValue) !== -1,
    );
    const filteredWixelProjects = siteListState.wixelProjectsList.filter(
      (project) => `${project.value}`.toLowerCase().indexOf(filterValue) !== -1,
    );

    // Add Wix Sites section
    if (filteredWixSites.length > 0) {
      options.push(
        listItemSectionBuilder({
          id: 'wix-sites-header',
          type: 'title',
          title: formatMessage({ id: 'boards.modal.add-files.sites-header' }),
        }),
      );
      options.push(...filteredWixSites);
    }

    if (isWixelProjectsVisible && filteredWixelProjects.length > 0) {
      if (filteredWixSites.length > 0) {
        options.push(listItemSectionBuilder({ id: 'divider', type: 'divider' }));
      }
      options.push(
        listItemSectionBuilder({
          id: 'wixel-projects-header',
          type: 'title',
          title: formatMessage({ id: 'boards.modal.add-files.wixel-projects-header' }),
        }),
      );
      options.push(...filteredWixelProjects);
    }

    return options;
  }, [
    isWixelProjectsVisible,
    siteListState.wixSitesList,
    siteListState.wixelProjectsList,
    siteSelectionState,
    formatMessage,
  ]);

  const openFolder = useCallback(
    (folder) => {
      clearSearch();
      openFolderFromProps(folder);
    },
    [openFolderFromProps, clearSearch],
  );

  const openSiteRootFolder = useCallback(() => {
    openFolder(folderPath[0]);
  }, [openFolder, folderPath]);

  const handleSelectNewSite = useCallback(
    ({ id }) => {
      // Search in both Wix sites and Wixel projects lists
      const newActiveSite =
        find<ISite>(propEq('id', id))(siteListState.wixSitesList) ||
        find<ISite>(propEq('id', id))(siteListState.wixelProjectsList);

      if (!newActiveSite) {
        return;
      }
      clearSearch();
      openSiteRootFolder();
      selectAllMediaTypes();

      updateSiteSelection(newActiveSite.value);
      updateSiteMediaToken(newActiveSite.siteMediaToken);
    },
    [siteListState, selectAllMediaTypes, clearSearch, openSiteRootFolder, updateSiteSelection, updateSiteMediaToken],
  );

  useEffect(() => {
    const { activeSite } = siteListState;
    if (activeSite && activeSite.value) {
      updateSiteSelection(activeSite.value);
    }
  }, [siteListState]);

  const isBreadcrumbsVisible = length(folderPath) > 1;
  const hasNoItems = !isLoading && !items.length;
  const isSiteSelectorHidden = any(isCurrentSiteOnlyBoard)(boards || []);

  const submitSelectedItems = useCallback(() => handleSelectItems(selectedItems), [handleSelectItems, selectedItems]);
  const submitItemOnDoubleClick = useCallback((item) => handleSelectItems([item]), [handleSelectItems]);

  const handleBreadcrumbsClick = useCallback(
    (folder) => {
      clearSearch();
      openFolder(folder);
    },
    [openFolder, clearSearch],
  );

  const handleItemClick = useCallback(
    (item, event: MouseEvent<HTMLElement>) => {
      const biParams = { tabName: `add_to_board` };

      const { id } = item;
      const { ctrlKey, metaKey, shiftKey } = event;

      if (ctrlKey || metaKey) {
        toggleItemSelection(id, biParams);
      } else if (shiftKey) {
        selectItemsUntil(id);
      } else {
        selectItem(id, biParams);
      }
    },
    [toggleItemSelection, selectItemsUntil, selectItem],
  );

  const handleSearchStart = useCallback(
    (searchQuery) => {
      openSiteRootFolder();

      setSearchQuery(searchQuery);
    },
    [openSiteRootFolder, setSearchQuery],
  );

  useEffect(() => {
    if (isNotAuthorized) {
      onSourceSessionExpired(source);
    }
  }, [isNotAuthorized, onSourceSessionExpired, source]);

  const isItemDisabled = (_item) => false;

  return (
    <div className={classnames(filesViewerStyles.wrapper, className)}>
      {!isSiteSelectorHidden && !isSitesListLoading && (
        <div className={styles.topRow}>
          <div className={styles.siteSelectionHolder}>
            <Text tagName="label" className={styles.chooseSitesLabel}>
              <FormattedMessage id="boards.modal.add-files.choose-sites.label" />
            </Text>

            <AutoComplete
              infiniteScroll
              dataHook="add-to-board-choose-sites"
              className={styles.siteSelection}
              options={combinedOptions}
              hasMore={hasMoreSites}
              loadMore={loadMoreSites}
              value={siteSelectionState}
              onChange={handleChangeSite}
              onSelect={handleSelectNewSite}
              popoverProps={{
                minWidth: 282,
                placement: 'bottom-start',
              }}
            />
          </div>

          <div className={filesViewerStyles.topRowActions}>
            <MediaTypeSelect
              mediaTypes={mediaTypes}
              onMediaTypeSelect={setMediaType}
              isDisabled={hasNoItems && !areNotAllMediaTypesSelected}
            />

            <div className={styles.actionsDivider} />

            <div className={styles.searchWrapper}>
              <Search
                value={searchQuery}
                onEnterPressed={handleSearchStart}
                onClearButtonClick={clearSearch}
                key={searchQuery}
                placeholder={formatMessage({ id: 'boards.modal.add-files.search.input-placeholder' })}
                isExpandable
              />
            </div>
          </div>
        </div>
      )}

      {isBreadcrumbsVisible && (
        <Breadcrumbs
          className={styles.breadcrumbs}
          folderPath={folderPath}
          source={source}
          onClick={handleBreadcrumbsClick}
          dropDisabled
        />
      )}

      {hasNoItems ? (
        <EmptyState
          source={ExternalSource.WixSite}
          isSearch={isSearch}
          searchQuery={searchQuery}
          isRoot={isRoot}
          isWixSitesRoot={false}
          areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
          clearAllExtendedFilters={selectAllMediaTypes}
        />
      ) : (
        <div className={filesViewerStyles.content}>
          <div className={classnames(filesViewerStyles.items, isLoading && filesViewerStyles.isLoadingItems)}>
            {isLoading ? (
              <div className={filesViewerStyles.loader}>
                <FullHeightLoader />
              </div>
            ) : (
              <Lasso
                itemMarginRight={LassoItemMargin[Layout.Gallery]}
                selectedItems={selectedItems}
                onSelectItems={selectItems}
              >
                {({ saveScrollingComponentWrapperRef, selectedAreaStyle }) => (
                  <div className={filesViewerStyles.scrollArea}>
                    <InfiniteScroll
                      ref={saveScrollingComponentWrapperRef}
                      scrollThreshold="400px"
                      height="100%"
                      dataLength={items.length}
                      hasMore={hasMoreItems}
                      next={loadMoreItems}
                      style={scrollStyle}
                      loader={null}
                    >
                      <FilesGallery
                        items={items}
                        isItemSelected={isItemSelected}
                        isItemDisabled={isItemDisabled}
                        onFolderDoubleClick={openFolder}
                        onFileDoubleClick={submitItemOnDoubleClick}
                        clearItemsSelection={clearItemsSelection}
                        onItemClick={handleItemClick}
                      />

                      {areNextFilesLoading && (
                        <div className={filesViewerStyles.loaderMore}>
                          <Loader size="small" />
                        </div>
                      )}

                      {selectedAreaStyle && (
                        <div
                          className={lassoSelectedAreaStyles.selectedArea}
                          style={selectedAreaStyle}
                          data-element-type="lasso"
                        />
                      )}
                    </InfiniteScroll>
                  </div>
                )}
              </Lasso>
            )}
          </div>

          <div className={styles.addButtonWrapper}>
            <Button
              size="small"
              disabled={!selectedItemsCount}
              onClick={submitSelectedItems}
              dataHook="add-files-to-board-add-button"
            >
              <FormattedMessage
                id="boards.modal.add-files.confirm"
                values={{
                  itemsCount: selectedItemsCount,
                }}
              />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
